import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { CollectionTab, CollectionConfig } from './tabs/CollectionTab';
import { ParsingTab, ParsingFieldSelection } from './tabs/ParsingTab';
import { useToast } from '@/hooks/use-toast';
import { AccountConfigurationData } from './ConfigurationModal';
import { Loader2 } from 'lucide-react';

export interface BulkAccountForConfig {
  id: string;
  account_number?: string;
  property_name?: string;
  unit_number?: string;
  commodity_type?: string;
}

interface BulkConfigurationModalProps {
  isOpen: boolean;
  onClose: () => void;
  accounts: BulkAccountForConfig[];
  projectId: string;
  workspaceId?: string;
  onSave: (config: Partial<AccountConfigurationData>, accountIds: string[]) => Promise<void>;
  isSaving?: boolean;
}

export const BulkConfigurationModal: React.FC<BulkConfigurationModalProps> = ({
  isOpen,
  onClose,
  accounts,
  projectId,
  workspaceId,
  onSave,
  isSaving = false,
}) => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('collection');
  const [applyToAll, setApplyToAll] = useState(true);

  // Collection Tab State
  const [collectionSource, setCollectionSource] = useState<'portal' | 'email' | 'manual' | undefined>();
  const [collectionFrequency, setCollectionFrequency] = useState('monthly');
  const [collectionConfig, setCollectionConfig] = useState<CollectionConfig>({});
  const [collectionCompleted, setCollectionCompleted] = useState(false);

  // Parsing Tab State
  const [parsingFields, setParsingFields] = useState<ParsingFieldSelection[]>([]);
  const [parsingCompleted, setParsingCompleted] = useState(false);

  const handleSave = async () => {
    try {
      const config: Partial<AccountConfigurationData> = {
        collection_source: collectionSource,
        collection_frequency: collectionFrequency,
        collection_config: collectionConfig,
        collection_completed: collectionCompleted,
        parsing_fields: parsingFields,
        parsing_completed: parsingCompleted,
        apply_to_property: false, // Bulk operations don't auto-apply to property
      };

      const accountIds = accounts.map(a => a.id);
      await onSave(config, accountIds);

      toast({
        title: 'Bulk Configuration Saved',
        description: `Configuration applied to ${accounts.length} account${accounts.length !== 1 ? 's' : ''}`,
      });

      onClose();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save bulk configuration',
        variant: 'destructive',
      });
    }
  };

  const isFormValid = () => {
    // Both collection and parsing must be configured
    const collectionValid = collectionSource && collectionCompleted;
    const parsingValid = parsingFields.length > 0 && parsingCompleted;
    return collectionValid && parsingValid;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Bulk Configure Parsing & Collection Mapping</DialogTitle>
          <DialogDescription>
            Configure parsing and collection mapping for {accounts.length} selected account{accounts.length !== 1 ? 's' : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Selected Accounts Preview */}
          <div className="p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Selected Accounts ({accounts.length})</span>
              <Badge variant="outline">{accounts.length} accounts</Badge>
            </div>
            <div className="max-h-32 overflow-y-auto space-y-1">
              {accounts.slice(0, 10).map((account) => (
                <div key={account.id} className="text-xs text-muted-foreground">
                  {account.account_number || account.id} - {account.property_name || 'N/A'}
                </div>
              ))}
              {accounts.length > 10 && (
                <div className="text-xs text-muted-foreground italic">
                  ...and {accounts.length - 10} more
                </div>
              )}
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="collection">Collection Mapping</TabsTrigger>
              <TabsTrigger value="parsing">Parsing Mapping</TabsTrigger>
            </TabsList>

            <TabsContent value="collection" className="space-y-4 mt-4">
              <CollectionTab
                collectionSource={collectionSource}
                setCollectionSource={setCollectionSource}
                collectionFrequency={collectionFrequency}
                setCollectionFrequency={setCollectionFrequency}
                collectionConfig={collectionConfig}
                setCollectionConfig={setCollectionConfig}
                applyToProperty={false}
                setApplyToProperty={() => {}}
                projectId={projectId}
                workspaceId={workspaceId}
              />
              <div className="flex items-center space-x-2 pt-4 border-t">
                <Checkbox
                  id="collection-completed"
                  checked={collectionCompleted}
                  onCheckedChange={(checked) => setCollectionCompleted(checked === true)}
                />
                <Label htmlFor="collection-completed" className="text-sm font-medium cursor-pointer">
                  Mark collection mapping as completed
                </Label>
              </div>
            </TabsContent>

            <TabsContent value="parsing" className="space-y-4 mt-4">
              <ParsingTab
                parsingFields={parsingFields}
                setParsingFields={setParsingFields}
                applyToProperty={false}
                setApplyToProperty={() => {}}
              />
              <div className="flex items-center space-x-2 pt-4 border-t">
                <Checkbox
                  id="parsing-completed"
                  checked={parsingCompleted}
                  onCheckedChange={(checked) => setParsingCompleted(checked === true)}
                />
                <Label htmlFor="parsing-completed" className="text-sm font-medium cursor-pointer">
                  Mark parsing mapping as completed
                </Label>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!isFormValid() || isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              `Save to ${accounts.length} Account${accounts.length !== 1 ? 's' : ''}`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

